/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * DSA Status Dashboard Card
 * 
 * Displays the status of Distributed System Agents (DSA) on the dashboard.
 * Shows worker status, recent runs, and quick access to DSA configuration.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Network, 
  RefreshCw, 
  Check, 
  X, 
  Clock, 
  Play,
  AlertCircle,
  ChevronRight,
  Settings
} from 'lucide-react';
import {
  getDSAStatus,
  getDSARuns,
  formatRelativeTime,
  getDSARunStatusColor,
  getDSAResultColor,
} from '../../lib/api/dsa';
import type {
  DSAStatusResponse,
  DSARun,
  DSARunsResponse,
} from '@shared/schema/dsa';

interface DSAStatusProps {
  isLoading?: boolean;
}

export function DSAStatus({ isLoading = false }: DSAStatusProps) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<DSAStatusResponse | null>(null);
  const [runs, setRuns] = useState<DSARunsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    loadData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);
  
  const loadData = async () => {
    setLoading(true);
    
    try {
      const [statusResult, runsResult] = await Promise.all([
        getDSAStatus(),
        getDSARuns(),
      ]);
      
      if (statusResult.success && statusResult.status) {
        setStatus(statusResult.status);
      }
      
      if (runsResult.success && runsResult.runs) {
        setRuns(runsResult.runs);
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to load DSA status');
    } finally {
      setLoading(false);
    }
  };
  
  const isEnabled = status?.worker?.enabled ?? false;
  const workerStatus = status?.worker;
  const activeRuns = runs?.active || [];
  const recentRuns = (runs?.completed || []).slice(0, 3);
  
  // If DSA is not enabled, show a compact "setup" card
  if (!loading && !isEnabled) {
    return (
      <div style={{
        background: '#f9f9f9',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Network size={18} style={{ color: '#9ca3af' }} />
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>
              Distributed Agents
            </h3>
          </div>
        </div>
        
        {/* Body */}
        <div style={{
          padding: '20px',
          textAlign: 'center'
        }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            borderRadius: '50%',
            background: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px'
          }}>
            <Network size={24} style={{ color: '#9ca3af' }} />
          </div>
          <p style={{ 
            margin: '0 0 16px', 
            fontSize: '13px', 
            color: '#6b7280',
            lineHeight: 1.5
          }}>
            Enable DSA to coordinate agents across your organization.
          </p>
          <button
            onClick={() => navigate('/settings')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              background: '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            <Settings size={14} />
            Configure DSA
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div style={{
      background: '#f9f9f9',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Network size={18} style={{ color: '#0066cc' }} />
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>
            Distributed Agents
          </h3>
        </div>
        
        {workerStatus && (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '3px 8px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: 500,
            background: '#dcfce7',
            color: '#16a34a',
            gap: '4px'
          }}>
            <span style={{ 
              width: '5px', 
              height: '5px', 
              borderRadius: '50%', 
              background: '#16a34a',
              animation: 'pulse 2s infinite'
            }} />
            Active
          </span>
        )}
      </div>
      
      {/* Body */}
      <div style={{ padding: '16px' }}>
        {loading || isLoading ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '20px',
            color: '#6b7280'
          }}>
            <RefreshCw size={20} className="spin" style={{ marginBottom: '8px' }} />
            <p style={{ margin: 0, fontSize: '13px' }}>Loading...</p>
          </div>
        ) : (
          <>
            {/* Worker Info */}
            {workerStatus && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '12px',
                  fontSize: '12px'
                }}>
                  <div>
                    <span style={{ color: '#6b7280' }}>Worker</span>
                    <p style={{ 
                      margin: '2px 0 0', 
                      fontFamily: 'monospace', 
                      fontSize: '11px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {workerStatus.workerId.slice(0, 16)}...
                    </p>
                  </div>
                  <div>
                    <span style={{ color: '#6b7280' }}>Last Poll</span>
                    <p style={{ margin: '2px 0 0' }}>
                      {formatRelativeTime(workerStatus.lastPoll)}
                    </p>
                  </div>
                </div>
                
                <div style={{
                  marginTop: '12px',
                  padding: '8px 12px',
                  background: '#f3f4f6',
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '12px'
                }}>
                  <span style={{ color: '#374151', fontWeight: 500 }}>
                    Runs Today
                  </span>
                  <span style={{ color: '#0066cc', fontWeight: 600 }}>
                    {workerStatus.runsToday}
                  </span>
                </div>
              </div>
            )}
            
            {/* Active Runs */}
            {activeRuns.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ 
                  margin: '0 0 8px', 
                  fontSize: '12px', 
                  fontWeight: 600,
                  color: '#374151' 
                }}>
                  Running Now
                </h4>
                {activeRuns.map(run => (
                  <div
                    key={run.runId}
                    style={{
                      padding: '10px',
                      background: '#eff6ff',
                      borderRadius: '6px',
                      border: '1px solid #bfdbfe',
                      marginBottom: '8px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <RefreshCw size={12} className="spin" style={{ color: '#2563eb' }} />
                      <span style={{ fontSize: '12px', fontWeight: 500, color: '#1d4ed8' }}>
                        {run.agentName}
                      </span>
                    </div>
                    {run.progress && (
                      <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#3b82f6' }}>
                        {run.progress}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* Recent Runs */}
            {recentRuns.length > 0 && (
              <div>
                <h4 style={{ 
                  margin: '0 0 8px', 
                  fontSize: '12px', 
                  fontWeight: 600,
                  color: '#374151' 
                }}>
                  Recent Runs
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {recentRuns.map(run => (
                    <div
                      key={run.runId}
                      style={{
                        padding: '8px 10px',
                        background: 'white',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {run.result === 'pass' && (
                          <Check size={14} style={{ color: '#16a34a' }} />
                        )}
                        {run.result === 'warning' && (
                          <AlertCircle size={14} style={{ color: '#f59e0b' }} />
                        )}
                        {run.result === 'fail' && (
                          <X size={14} style={{ color: '#dc2626' }} />
                        )}
                        <span style={{ fontSize: '12px', fontWeight: 500 }}>
                          {run.agentName}
                        </span>
                      </div>
                      <span style={{ 
                        fontSize: '11px', 
                        color: '#6b7280'
                      }}>
                        {formatRelativeTime(run.completedAt || run.startedAt)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Empty state */}
            {activeRuns.length === 0 && recentRuns.length === 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '16px',
                color: '#6b7280'
              }}>
                <Clock size={20} style={{ marginBottom: '8px', opacity: 0.5 }} />
                <p style={{ margin: 0, fontSize: '12px' }}>
                  No recent runs
                </p>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Footer */}
      <div style={{
        padding: '12px 16px',
        borderTop: '1px solid #e5e7eb',
        background: 'white'
      }}>
        <button
          onClick={() => navigate('/agents')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            gap: '6px',
            padding: '8px',
            background: 'transparent',
            border: 'none',
            color: '#0066cc',
            fontSize: '12px',
            fontWeight: 500,
            cursor: 'pointer',
            borderRadius: '4px'
          }}
        >
          View All Agents
          <ChevronRight size={14} />
        </button>
      </div>
      
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
